import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { format, startOfDay, endOfDay } from 'date-fns'
import {
  getServicosAdmin,
  getProfissionais,
  getProfissionalServicosMap,
  getConfiguracoes,
  criarAgendamento,
  deleteAgendamento,
  getSalasDaCategoria,
  escolherSalaLivre,
  upsertCliente,
  pesquisarClientes,
  getAgendamentos,
  getBloqueiosDoDia,
} from '../../lib/supabase'
import { validarTelefone } from '../../lib/validacoes'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { useResponsive } from '../../hooks/useResponsive'
import Loading from '../../components/Loading'
import FaixaAgendaDia from '../../components/FaixaAgendaDia'

// min-width: 0 é essencial aqui — sem isto, dentro de um item de grid/flex,
// o Safari/iOS deixa o "min-width: auto" por omissão vencer o width:100% para
// inputs date/time (o controlo nativo reporta um conteúdo mínimo maior do que
// o Chromium), empurrando a página inteira para scroll horizontal em mobile.
const inputStyle = {
  width: '100%', minWidth: 0, padding: '10px 36px 10px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

const inputStyleSimples = {
  width: '100%', minWidth: 0, padding: '10px 12px',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  fontSize: '14px', fontFamily: 'var(--font-ui)',
  background: 'var(--cor-branco)', color: 'var(--cor-texto)',
}

// Moldura (borda/fundo/cantos) para os inputs date/time — fica no <div>
// exterior, nunca no <input>, para o corte de overflow (ver mais abaixo)
// nunca cortar visualmente a borda. A classe "input-nativo-moldura" no
// global.css dá o estado de foco (o input em si fica sem borda própria).
const molduraInputNativo = {
  width: '100%', maxWidth: '100%', overflow: 'hidden',
  border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
  background: 'var(--cor-branco)',
}
const inputStyleSemMoldura = {
  width: '100%', minWidth: 0, display: 'block',
  padding: '10px 36px 10px 12px',
  border: 'none', outline: 'none', background: 'transparent',
  fontSize: '14px', fontFamily: 'var(--font-ui)', color: 'var(--cor-texto)',
}

let proximaChave = 1
function novaLinhaServico() {
  return { chave: proximaChave++, servico_id: '', sala_id: '' }
}

// Estado inicial calculado no momento em que a página monta — assim a data e a
// hora predefinidas são as do momento de abertura (e não as do arranque da app).
function vazio() {
  return {
    servicos: [novaLinhaServico()],
    profissional_id: '',
    data: format(new Date(), 'yyyy-MM-dd'),
    // Hora atual arredondada ao múltiplo de 5 min (o input usa step de 5 min).
    hora: format(new Date(Math.ceil(Date.now() / 300000) * 300000), 'HH:mm'),
    cliente_nome: '',
    cliente_email: '',
    cliente_telefone: '',
    cliente_nif: '',
    notas: '',
  }
}

function Campo({ label, children, obrigatorio }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
        {label}{obrigatorio && ' *'}
      </label>
      {children}
    </div>
  )
}

export default function NovaMarcacao() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { tenant } = useTenant()
  const toast = useToast()
  const { isDesktop } = useResponsive()
  const [carregando, setCarregando] = useState(true)
  const [servicos, setServicos] = useState([])
  const [profissionais, setProfissionais] = useState([])
  const [profissionalServicosMap, setProfissionalServicosMap] = useState({})
  const [horarioTenant, setHorarioTenant] = useState({
    abertura: '09:00', fecho: '18:00',
    almocoAtivo: false, almocoInicio: '13:00', almocoFim: '14:00',
  })
  // Salas por categoria, em cache — evita repetir o pedido quando duas linhas
  // de serviço partilham a mesma categoria.
  const [salasPorCategoria, setSalasPorCategoria] = useState({})
  const [form, setForm] = useState(vazio)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!tenant) return
    Promise.all([
      getServicosAdmin(tenant.id),
      getProfissionais(tenant.id),
      getProfissionalServicosMap(tenant.id),
      getConfiguracoes(tenant.id),
    ])
      .then(([svcs, profs, mapa, cfg]) => {
        setServicos(svcs)
        setProfissionais(profs)
        setProfissionalServicosMap(mapa)
        setHorarioTenant({
          abertura: cfg.hora_abertura || '09:00',
          fecho: cfg.hora_fecho || '18:00',
          almocoAtivo: cfg.almoco_ativo === 'true',
          almocoInicio: cfg.hora_almoco_inicio || '13:00',
          almocoFim: cfg.hora_almoco_fim || '14:00',
        })
        // Pré-preencher a partir dos query params vindos da agenda
        const profId = searchParams.get('profissional')
        const data = searchParams.get('data')
        const hora = searchParams.get('hora')
        if (profId || data || hora) {
          setForm(f => ({
            ...f,
            ...(profId && profs.some(p => p.id === profId) ? { profissional_id: profId } : {}),
            ...(data ? { data } : {}),
            ...(hora ? { hora } : {}),
          }))
        }
      })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  const servicosPorId = useMemo(() => {
    const mapa = {}
    servicos.forEach(s => { mapa[s.id] = s })
    return mapa
  }, [servicos])

  // Quando há profissional selecionada, restringe as opções aos serviços que
  // ela executa (`profissional_servicos`). Sem profissional, mostra tudo.
  // Um serviço já escolhido antes de escolher a profissional não é apagado —
  // fica na lista (mesmo fora do conjunto habitual dela) para não perder a
  // selecção do utilizador; ver `servicoForaDaLista` para o aviso.
  const idsServicosProfissional = useMemo(() => (
    form.profissional_id ? (profissionalServicosMap[form.profissional_id] || []) : null
  ), [form.profissional_id, profissionalServicosMap])

  const idsServicosEscolhidos = useMemo(
    () => form.servicos.map(l => l.servico_id).filter(Boolean),
    [form.servicos]
  )

  const servicosDoProfissional = useMemo(() => {
    if (!idsServicosProfissional) return servicos
    const filtrados = servicos.filter(s => idsServicosProfissional.includes(s.id))
    const faltam = idsServicosEscolhidos.filter(
      id => !filtrados.some(s => s.id === id) && servicosPorId[id]
    )
    if (faltam.length === 0) return filtrados
    return [...filtrados, ...faltam.map(id => servicosPorId[id])]
  }, [servicos, idsServicosProfissional, idsServicosEscolhidos, servicosPorId])

  const categorias = [...new Set(servicosDoProfissional.map(s => s.categoria))]

  function set(campo, valor) {
    setForm(prev => ({ ...prev, [campo]: valor }))
  }

  function definirLinhaServico(chave, patch) {
    setForm(prev => ({
      ...prev,
      servicos: prev.servicos.map(l => l.chave === chave ? { ...l, ...patch } : l),
    }))
  }

  function adicionarServico() {
    setForm(prev => ({ ...prev, servicos: [...prev.servicos, novaLinhaServico()] }))
  }

  function removerServico(chave) {
    setForm(prev => ({
      ...prev,
      servicos: prev.servicos.length > 1 ? prev.servicos.filter(l => l.chave !== chave) : prev.servicos,
    }))
  }

  // Busca as salas da categoria de um serviço (com cache) sempre que uma
  // linha escolhe/muda de serviço.
  useEffect(() => {
    if (!tenant) return
    const categoriasEmFalta = [...new Set(
      form.servicos
        .map(l => servicosPorId[l.servico_id]?.categoria)
        .filter(cat => cat && !(cat in salasPorCategoria))
    )]
    if (categoriasEmFalta.length === 0) return
    Promise.all(categoriasEmFalta.map(cat => getSalasDaCategoria(tenant.id, cat)))
      .then(resultados => {
        setSalasPorCategoria(prev => {
          const novo = { ...prev }
          categoriasEmFalta.forEach((cat, i) => { novo[cat] = resultados[i] })
          return novo
        })
      })
      .catch(e => toast.erro(e.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, form.servicos, servicosPorId])

  // A hora de início é definida livremente pelo utilizador (input type="time");
  // as seguintes calculam-se em cascata a partir da duração de cada serviço.
  const slotInicial = useMemo(() => (
    (form.data && /^\d{2}:\d{2}$/.test(form.hora))
      ? new Date(`${form.data}T${form.hora}:00`)
      : null
  ), [form.data, form.hora])

  const linhasComHorario = useMemo(() => {
    if (!slotInicial) return []
    let cursor = slotInicial
    return form.servicos.map(linha => {
      const servico = servicosPorId[linha.servico_id] || null
      if (!servico) return { ...linha, servico: null, inicio: null, fim: null }
      const inicio = cursor
      const fim = new Date(cursor.getTime() + servico.duracao_min * 60000)
      cursor = fim
      return { ...linha, servico, inicio, fim }
    })
  }, [form.servicos, slotInicial, servicosPorId])

  const linhasValidas = linhasComHorario.filter(l => l.servico)
  const slotFimTotal = linhasValidas.length > 0 ? linhasValidas[linhasValidas.length - 1].fim : null

  // Blocos provisórios para a faixa lateral — só os serviços já escolhidos.
  const blocosProvisorios = useMemo(() => linhasValidas.map(l => ({
    chave: l.chave,
    inicio: l.inicio,
    fim: l.fim,
    nome: l.servico.nome,
  })), [linhasValidas])

  // Dados brutos do dia (marcações + bloqueios), partilhados entre o aviso de
  // conflitos e a faixa lateral — um único pedido de rede por mudança de dia.
  const [agendamentosDoDia, setAgendamentosDoDia] = useState([])
  const [bloqueiosDoDia, setBloqueiosDoDia] = useState([])
  useEffect(() => {
    if (!tenant || !form.data) { setAgendamentosDoDia([]); setBloqueiosDoDia([]); return }
    let ativo = true
    const dia = new Date(`${form.data}T12:00:00`)
    const diaInicio = startOfDay(dia).toISOString()
    const diaFim = endOfDay(dia).toISOString()
    Promise.all([
      // Cancelados incluídos como na Agenda do Dia — a faixa mostra-os (ou
      // não) conforme os estados visíveis ao perfil.
      getAgendamentos(tenant.id, diaInicio, diaFim, { incluirCancelados: true }),
      getBloqueiosDoDia(tenant.id, diaInicio, diaFim),
    ])
      .then(([ags, bloqs]) => {
        if (!ativo) return
        setAgendamentosDoDia(ags)
        setBloqueiosDoDia(bloqs)
      })
      .catch(() => { if (ativo) { setAgendamentosDoDia([]); setBloqueiosDoDia([]) } })
    return () => { ativo = false }
  }, [tenant, form.data])

  // Aviso não-bloqueante: verifica se a sequência escolhida se sobrepõe a
  // marcações ou bloqueios já existentes do profissional (ou bloqueios
  // globais do espaço). O intervalo composto é contíguo (sem buracos), por
  // isso basta verificar contra o início do 1º e o fim do último serviço.
  const conflitos = useMemo(() => {
    if (!form.profissional_id || !slotInicial || !slotFimTotal) return []
    const sobrepoe = (ini, f) => new Date(ini) < slotFimTotal && new Date(f) > slotInicial
    const lista = []
    agendamentosDoDia
      .filter(a => a.profissional_id === form.profissional_id)
      // Uma marcação cancelada não ocupa a agenda — não é conflito.
      .filter(a => a.estado !== 'cancelado')
      .filter(a => sobrepoe(a.data_hora_inicio, a.data_hora_fim))
      .forEach(a => lista.push({
        tipo: 'marcação',
        label: `${format(new Date(a.data_hora_inicio), 'HH:mm')}–${format(new Date(a.data_hora_fim), 'HH:mm')} · ${a.cliente_nome || 'Sem nome'}`,
      }))
    bloqueiosDoDia
      .filter(b => b.profissional_id === null || b.profissional_id === form.profissional_id)
      .filter(b => sobrepoe(b.data_hora_inicio, b.data_hora_fim))
      .forEach(b => {
        // Um bloqueio de vários dias (férias, p. ex.) ocupa o dia todo: mostrar
        // só "09:00–18:00" das pontas seria enganador, porque essas horas são
        // de dias diferentes.
        const ini = new Date(b.data_hora_inicio)
        const f = new Date(b.data_hora_fim)
        const variosDias = format(ini, 'yyyy-MM-dd') !== format(f, 'yyyy-MM-dd')
        const periodo = variosDias
          ? `${format(ini, 'dd/MM HH:mm')} – ${format(f, 'dd/MM HH:mm')} (dia inteiro)`
          : `${format(ini, 'HH:mm')}–${format(f, 'HH:mm')}`
        lista.push({
          tipo: b.profissional_id === null ? 'bloqueio do espaço' : 'bloqueio',
          label: `${periodo}${b.motivo ? ` · ${b.motivo}` : ''}`,
        })
      })
    return lista
  }, [form.profissional_id, slotInicial, slotFimTotal, agendamentosDoDia, bloqueiosDoDia])

  // Profissional "vista" na faixa lateral — por omissão acompanha a do
  // formulário, mas o utilizador pode trocar de aba sem alterar a marcação.
  const [profissionalVistoId, setProfissionalVistoId] = useState('')
  useEffect(() => {
    if (form.profissional_id) setProfissionalVistoId(form.profissional_id)
  }, [form.profissional_id])

  function aoClicarSlot(horaHHmm, profId) {
    setForm(prev => ({
      ...prev,
      hora: horaHHmm,
      ...(profId && profId !== prev.profissional_id ? { profissional_id: profId } : {}),
    }))
    if (profId) setProfissionalVistoId(profId)
  }

  // Pesquisa de cliente com paginação infinite scroll
  const [termoPesquisa, setTermoPesquisa] = useState('')
  const [resultadosPesquisa, setResultadosPesquisa] = useState([])
  const [pesquisando, setPesquisando] = useState(false)
  const [carregandoMais, setCarregandoMais] = useState(false)
  const [dropdownAberto, setDropdownAberto] = useState(false)
  const [paginaAtual, setPaginaAtual] = useState(0)
  const [temMais, setTemMais] = useState(false)
  const pesquisaRef = useRef(null)
  const dropdownRef = useRef(null)
  const termoAtual = useRef('')
  const pesquisaAtivada = useRef(false)

  const pesquisar = useCallback((termo, pagina = 0) => {
    const t = termo?.trim() || ''
    if (pagina === 0) {
      setPesquisando(true)
      setResultadosPesquisa([])
    } else {
      setCarregandoMais(true)
    }
    termoAtual.current = t
    pesquisarClientes(tenant.id, t, pagina)
      .then(res => {
        if (termoAtual.current !== t) return // descarta resultado de pesquisa antiga
        setResultadosPesquisa(prev => pagina === 0 ? res : [...prev, ...res])
        setTemMais(res.length === 15)
        setPaginaAtual(pagina)
        setDropdownAberto(true)
      })
      .catch(() => {})
      .finally(() => { setPesquisando(false); setCarregandoMais(false) })
  }, [tenant])

  // Detecta scroll no fundo do dropdown para carregar mais
  const aoScrollDropdown = useCallback((e) => {
    const el = e.currentTarget
    if (!temMais || carregandoMais || pesquisando) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      pesquisar(termoPesquisa, paginaAtual + 1)
    }
  }, [temMais, carregandoMais, pesquisando, termoPesquisa, paginaAtual, pesquisar])

  useEffect(() => {
    function fechar(e) {
      if (pesquisaRef.current && !pesquisaRef.current.contains(e.target)) {
        setDropdownAberto(false)
      }
    }
    document.addEventListener('mousedown', fechar)
    return () => document.removeEventListener('mousedown', fechar)
  }, [])

  // Pesquisa automática enquanto digita (debounce 300ms) — só após activação manual
  useEffect(() => {
    if (!pesquisaAtivada.current) return
    const timer = setTimeout(() => pesquisar(termoPesquisa, 0), 300)
    return () => clearTimeout(timer)
  }, [termoPesquisa]) // eslint-disable-line react-hooks/exhaustive-deps

  function preencherComCliente(cliente) {
    const nomeDisplay = cliente.nome_amigavel || cliente.nome
    setForm(prev => ({
      ...prev,
      cliente_nome: nomeDisplay,
      cliente_telefone: cliente.telefone || '',
      cliente_email: cliente.email || '',
      cliente_nif: cliente.nif || '',
    }))
    setTermoPesquisa(nomeDisplay)
    setDropdownAberto(false)
    setResultadosPesquisa([])
  }

  const [confirmarSemTelefone, setConfirmarSemTelefone] = useState(false)
  const [erroTelefone, setErroTelefone] = useState(null)
  const semTelefoneConfirmado = React.useRef(false)

  const multiplosServicos = linhasValidas.length > 1

  const formValido =
    linhasValidas.length > 0 &&
    form.profissional_id &&
    form.data &&
    slotInicial &&
    form.cliente_nome.trim() &&
    (!multiplosServicos || form.cliente_telefone.trim())

  async function submeter(e) {
    e.preventDefault()
    if (!formValido) return
    if (form.cliente_telefone.trim() && !validarTelefone(form.cliente_telefone)) {
      setErroTelefone('Número inválido. Use 9XXXXXXXX ou formato internacional (+44…)')
      return
    }
    setErroTelefone(null)
    if (!multiplosServicos && !form.cliente_telefone.trim() && !semTelefoneConfirmado.current) {
      setConfirmarSemTelefone(true)
      return
    }
    semTelefoneConfirmado.current = false
    setConfirmarSemTelefone(false)
    setEnviando(true)

    const criados = []
    try {
      const cliente_id = form.cliente_telefone.trim()
        ? await upsertCliente(tenant.id, {
            nome: form.cliente_nome.trim(),
            telefone: form.cliente_telefone.trim(),
            email: form.cliente_email.trim() || null,
            nif: form.cliente_nif.trim() || null,
          })
        : null

      for (const linha of linhasValidas) {
        const sala_id = linha.sala_id
          || await escolherSalaLivre(tenant.id, linha.servico.categoria, linha.inicio, linha.fim)

        const ag = await criarAgendamento(tenant.id, {
          servico_id: linha.servico_id,
          profissional_id: form.profissional_id,
          sala_id,
          cliente_id,
          cliente_nome: form.cliente_nome.trim(),
          cliente_email: form.cliente_email.trim() || null,
          cliente_telefone: form.cliente_telefone.trim() || null,
          data_hora_inicio: linha.inicio.toISOString(),
          data_hora_fim: linha.fim.toISOString(),
          notas: form.notas.trim() || null,
          newsletter_opt_in: false,
        })
        criados.push(ag)
      }

      toast.sucesso(criados.length > 1 ? `${criados.length} marcações criadas.` : 'Marcação criada.')

      // Com um único serviço vai para o detalhe (é lá que se cobra); com
      // vários não há um "detalhe do conjunto" — volta para a agenda do dia.
      navigate(criados.length === 1
        ? `/admin/agendamento/${criados[0].id}`
        : `/admin?data=${form.data}`)
    } catch (e) {
      // Sem RPC transacional para N inserts — desfazer as já criadas.
      for (const ag of criados.reverse()) {
        try { await deleteAgendamento(tenant.id, ag.id) } catch { /* ignora falha ao desfazer */ }
      }
      toast.erro(e.message)
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) {
    return <div style={{ padding: '64px 24px' }}><Loading /></div>
  }

  return (
    // Ocupa a largura toda disponível (com um tecto generoso) e, em desktop,
    // fixa a altura ao viewport: a página deixa de fazer scroll global e cada
    // coluna trata do seu próprio scroll.
    <main style={{
      maxWidth: isDesktop ? '1700px' : '700px',
      margin: '0 auto',
      padding: isDesktop ? '20px 24px 20px' : '24px 16px 48px',
      boxSizing: 'border-box',
      // Só em desktop é que a página se fixa à altura do ecrã (cada coluna com
      // o seu scroll). Em mobile/tablet mantém-se o fluxo normal do documento,
      // com o scroll da página — é o .admin-content que rola.
      ...(isDesktop
        ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }
        : {}),
    }}>
      <h2 style={{ fontSize: '22px', marginBottom: isDesktop ? '14px' : '20px', flexShrink: 0 }}>Nova Marcação</h2>

      <div style={{
        display: 'flex',
        gap: '20px',
        alignItems: 'stretch',
        ...(isDesktop ? { flex: 1, minHeight: 0 } : { flexDirection: 'column' }),
      }}>
        <form
          id="form-nova-marcacao"
          onSubmit={submeter}
          style={{
            flex: '1 1 auto', minWidth: 0,
            display: 'flex', flexDirection: 'column', gap: '16px',
            // Scroll próprio do formulário, para o cabeçalho e a agenda
            // ficarem sempre à vista.
            ...(isDesktop ? { overflowY: 'auto', paddingRight: '4px', minHeight: 0 } : {}),
          }}
        >
          {/* Em ecrãs largos as duas secções ficam lado a lado, aproveitando a
              largura em vez de empilharem e obrigarem a scroll. */}
          <div className="nm-colunas" style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Serviços + Profissional + Data */}
          <section style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '18px', flex: '1 1 380px', minWidth: 0 }}>
            <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '14px' }}>Serviços</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="grid-2col">
              <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <Campo label="Profissional" obrigatorio>
                  <select
                    value={form.profissional_id}
                    onChange={e => set('profissional_id', e.target.value)}
                    style={inputStyle}
                    required
                  >
                    <option value="">— escolher —</option>
                    {profissionais.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </Campo>
              </div>
              <Campo label="Data" obrigatorio>
                {/* A borda/fundo estão no <div>, não no <input>: no Safari/iOS
                    o controlo nativo de <input type="date"> por vezes
                    desenha-se mais largo do que a caixa CSS pedida, ignorando
                    width:100%. O overflow:hidden aqui esconde esse excesso em
                    vez de deixar a página inteira ir a scroll horizontal —
                    e por estar no <div>, a borda em si nunca fica cortada. */}
                <div className="input-nativo-moldura" style={molduraInputNativo}>
                  <input
                    type="date"
                    value={form.data}
                    onChange={e => set('data', e.target.value)}
                    style={inputStyleSemMoldura}
                    required
                  />
                </div>
              </Campo>
              <Campo label="Hora de início" obrigatorio>
                <div className="input-nativo-moldura" style={molduraInputNativo}>
                  <input
                    type="time"
                    value={form.hora}
                    onChange={e => set('hora', e.target.value)}
                    step={300}
                    style={inputStyleSemMoldura}
                    required
                  />
                </div>
              </Campo>
            </div>

            {/* Lista de serviços em sequência — cada um começa onde o anterior acaba */}
            <div style={{ marginTop: '18px', borderTop: '1px solid var(--cor-borda)', paddingTop: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {linhasComHorario.map((linha, i) => {
                  const idsProfSet = idsServicosProfissional
                  const foraDaLista = !!(idsProfSet && linha.servico_id && !idsProfSet.includes(linha.servico_id))
                  const salasLinha = linha.servico ? (salasPorCategoria[linha.servico.categoria] || []) : []
                  return (
                    <div key={linha.chave} style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '8px', minWidth: 0, alignItems: 'flex-start' }}>
                        <select
                          value={linha.servico_id}
                          onChange={e => definirLinhaServico(linha.chave, { servico_id: e.target.value, sala_id: '' })}
                          style={{ ...inputStyleSimples, flex: '1 1 auto', minWidth: 0 }}
                          required={i === 0}
                        >
                          <option value="">— escolher serviço —</option>
                          {categorias.map(cat => (
                            <optgroup key={cat} label={cat}>
                              {servicosDoProfissional.filter(s => s.categoria === cat).map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.nome} ({s.duracao_min} min)
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <select
                          value={linha.sala_id}
                          onChange={e => definirLinhaServico(linha.chave, { sala_id: e.target.value })}
                          style={{ ...inputStyleSimples, flex: '1 1 120px', minWidth: 0 }}
                          disabled={!linha.servico}
                        >
                          <option value="">
                            {!linha.servico
                              ? 'Sala'
                              : salasLinha.length === 0
                                ? 'Sem salas'
                                : 'Automática'}
                          </option>
                          {salasLinha.map(s => (
                            <option key={s.id} value={s.id}>{s.nome}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removerServico(linha.chave)}
                          disabled={form.servicos.length === 1}
                          title="Remover serviço"
                          style={{
                            flex: '0 0 38px',
                            width: '38px', height: '38px',
                            border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)',
                            background: 'var(--cor-branco)',
                            color: form.servicos.length === 1 ? 'var(--cor-texto-suave)' : '#DC2626',
                            cursor: form.servicos.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: form.servicos.length === 1 ? 0.4 : 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      {foraDaLista && (
                        <p style={{ fontSize: '12px', color: '#92400E', marginTop: '4px' }}>
                          Este serviço não está atribuído a esta profissional.
                        </p>
                      )}
                      {linha.inicio && linha.fim && (
                        <p style={{ fontSize: '12px', color: 'var(--cor-texto-suave)', marginTop: '4px' }}>
                          {format(linha.inicio, 'HH:mm')}–{format(linha.fim, 'HH:mm')}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>

              {idsServicosProfissional && idsServicosProfissional.length === 0 && (
                <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '10px' }}>
                  Esta profissional não tem serviços atribuídos.
                </p>
              )}

              <button
                type="button"
                onClick={adicionarServico}
                style={{
                  marginTop: '12px',
                  background: 'none', border: 'none', padding: 0,
                  color: 'var(--cor-principal)', fontWeight: '600', fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                + Adicionar serviço (após o anterior)
              </button>

              {slotInicial && slotFimTotal && linhasValidas.length > 0 ? (
                <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginTop: '14px' }}>
                  Das <strong>{format(slotInicial, 'HH:mm')}</strong> às <strong>{format(slotFimTotal, 'HH:mm')}</strong>
                  {linhasValidas.length > 1 ? ` (${linhasValidas.length} serviços seguidos)` : ''}
                </p>
              ) : (
                <p style={{ fontSize: '13px', color: 'var(--cor-texto-suave)', marginTop: '14px' }}>
                  Escolha pelo menos um serviço para calcular os horários.
                </p>
              )}

              {/* Aviso não-bloqueante de sobreposição — a marcação pode mesmo
                  assim ser criada. */}
              {conflitos.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: 'var(--raio)',
                  padding: '10px 12px',
                }}>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#92400E', margin: 0 }}>
                    ⚠️ Sobreposição com {conflitos.length === 1 ? '1 item' : `${conflitos.length} itens`} neste horário
                  </p>
                  <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                    {conflitos.map((c, i) => (
                      <li key={i} style={{ fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
                        <span style={{ textTransform: 'capitalize' }}>{c.tipo}</span>: {c.label}
                      </li>
                    ))}
                  </ul>
                  <p style={{ fontSize: '11px', color: '#92400E', margin: '6px 0 0', opacity: 0.85 }}>
                    Podes criar a marcação à mesma.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Coluna do cliente: a ficha e, logo por baixo, os botões — para
              não acompanharem a altura da coluna dos serviços (que cresce a
              cada serviço adicionado). */}
          <div className="nm-coluna-cliente" style={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <section style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '18px' }}>
            <p style={{ fontWeight: '600', color: 'var(--cor-principal)', marginBottom: '14px' }}>Cliente</p>

            {/* Pesquisa de cliente existente */}
            <div ref={pesquisaRef} style={{ position: 'relative', marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                Pesquisar cliente existente
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={termoPesquisa}
                  onChange={e => setTermoPesquisa(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setDropdownAberto(false) }}
                  onFocus={() => {
                    pesquisaAtivada.current = true
                    if (resultadosPesquisa.length > 0) setDropdownAberto(true)
                    else pesquisar(termoPesquisa, 0)
                  }}
                  placeholder="Nome, telefone ou NIF…"
                  style={{ ...inputStyleSimples, paddingRight: '36px' }}
                  autoComplete="off"
                />
                <span style={{
                  position: 'absolute', right: '11px', top: '50%', transform: 'translateY(-50%)',
                  fontSize: '14px', pointerEvents: 'none', opacity: 0.5,
                }}>
                  {pesquisando ? '…' : '🔍'}
                </span>
              </div>
              {dropdownAberto && (
                <ul
                  ref={dropdownRef}
                  onScroll={aoScrollDropdown}
                  style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: 'var(--cor-branco)',
                    border: '1px solid var(--cor-borda)',
                    borderRadius: 'var(--raio)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    listStyle: 'none',
                    margin: '2px 0 0',
                    padding: '4px 0',
                    maxHeight: '280px',
                    overflowY: 'auto',
                  }}
                >
                  {resultadosPesquisa.length === 0 && !pesquisando ? (
                    <li style={{ padding: '12px 14px', fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                      Nenhum cliente encontrado.
                    </li>
                  ) : (
                    resultadosPesquisa.map(c => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onMouseDown={() => preencherComCliente(c)}
                          style={{
                            width: '100%', textAlign: 'left',
                            padding: '10px 14px',
                            background: 'none', border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: 'var(--cor-texto)',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--cor-creme)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <span style={{ fontWeight: '500' }}>{c.nome_amigavel || c.nome}</span>
                          {c.telefone && (
                            <span style={{ color: 'var(--cor-texto-suave)', marginLeft: '8px', fontSize: '13px' }}>
                              {c.telefone}
                            </span>
                          )}
                          {c.nif && (
                            <span style={{ color: 'var(--cor-texto-suave)', marginLeft: '8px', fontSize: '12px' }}>
                              NIF {c.nif}
                            </span>
                          )}
                        </button>
                      </li>
                    ))
                  )}
                  {carregandoMais && (
                    <li style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--cor-texto-suave)', textAlign: 'center' }}>
                      A carregar…
                    </li>
                  )}
                  {temMais && !carregandoMais && (
                    <li style={{ padding: '8px 14px', fontSize: '11px', color: 'var(--cor-texto-suave)', textAlign: 'center', opacity: 0.6 }}>
                      Scroll para ver mais
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="grid-2col">
              <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <Campo label="Nome" obrigatorio>
                  <input type="text" value={form.cliente_nome} onChange={e => set('cliente_nome', e.target.value)} style={inputStyleSimples} required />
                </Campo>
              </div>
              <Campo label="Telefone" obrigatorio={multiplosServicos}>
                <input
                  type="tel"
                  value={form.cliente_telefone}
                  onChange={e => { set('cliente_telefone', e.target.value); setErroTelefone(null) }}
                  placeholder="912 345 678"
                  inputMode="tel"
                  style={{ ...inputStyleSimples, ...(erroTelefone ? { borderColor: '#DC2626' } : {}) }}
                />
                {erroTelefone && <p style={{ color: '#DC2626', fontSize: '12px', marginTop: '4px' }}>{erroTelefone}</p>}
              </Campo>
              <Campo label="NIF">
                <input type="text" value={form.cliente_nif} onChange={e => set('cliente_nif', e.target.value)} maxLength={9} inputMode="numeric" style={inputStyleSimples} placeholder="123456789" />
              </Campo>
              <Campo label="Email">
                <input type="email" value={form.cliente_email} onChange={e => set('cliente_email', e.target.value)} style={inputStyleSimples} />
              </Campo>
              <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <Campo label="Notas">
                  <textarea
                    value={form.notas}
                    onChange={e => set('notas', e.target.value)}
                    maxLength={500}
                    style={{ ...inputStyleSimples, minHeight: '70px', resize: 'vertical' }}
                  />
                </Campo>
              </div>
            </div>
          </section>

          <div className="acao-form" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => navigate('/admin')} className="btn-secundario">
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primario"
              disabled={!formValido || enviando}
              style={{ opacity: (!formValido || enviando) ? 0.6 : 1 }}
            >
              {enviando ? 'A criar…' : linhasValidas.length > 1 ? `Criar ${linhasValidas.length} marcações` : 'Criar marcação'}
            </button>
          </div>
          </div>
          </div>

          {confirmarSemTelefone && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px',
            }}>
              <div style={{
                background: 'var(--cor-branco)',
                borderRadius: 'var(--raio-lg)',
                padding: '28px 24px',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              }}>
                <p style={{ fontWeight: '600', fontSize: '16px', marginBottom: '10px' }}>Criar sem perfil de cliente?</p>
                <p style={{ fontSize: '14px', color: 'var(--cor-texto-suave)', marginBottom: '24px', lineHeight: '1.5' }}>
                  Sem telefone não é possível criar o perfil de cliente. A marcação será criada normalmente mas não ficará associada a nenhum cliente na base de dados.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn-secundario"
                    onClick={() => setConfirmarSemTelefone(false)}
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    className="btn-primario"
                    onClick={() => {
                      semTelefoneConfirmado.current = true
                      setConfirmarSemTelefone(false)
                      document.getElementById('form-nova-marcacao').requestSubmit()
                    }}
                  >
                    Criar mesmo assim
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Só em desktop — em mobile/tablet a faixa fica escondida (a agenda
            do dia inteira não cabe de forma legível num ecrã estreito e o
            objetivo aqui é só um atalho visual, não substituir a Agenda do
            Dia). */}
        {isDesktop && (
          <aside className="faixa-lateral-agenda" style={{ display: 'flex', flexDirection: 'column', flex: '0 0 400px', minWidth: 0, minHeight: 0 }}>
            <FaixaAgendaDia
              profissionais={profissionais}
              profissionalVistoId={profissionalVistoId}
              onMudarProfissionalVisto={setProfissionalVistoId}
              data={form.data}
              horaSelecionada={form.hora}
              horarioAbertura={horarioTenant.abertura}
              horarioFecho={horarioTenant.fecho}
              almocoAtivo={horarioTenant.almocoAtivo}
              horaAlmocoInicio={horarioTenant.almocoInicio}
              horaAlmocoFim={horarioTenant.almocoFim}
              agendamentosDoDia={agendamentosDoDia}
              bloqueiosDoDia={bloqueiosDoDia}
              blocosProvisorios={blocosProvisorios}
              onClicarSlot={aoClicarSlot}
            />
          </aside>
        )}
      </div>
    </main>
  )
}
